/**
 * Referral banner — shown on caregiver dashboard.
 * Displays live progress toward the $50 Amazon gift card:
 *   5 referrals + 80 combined hours → claim button lights up.
 */
import React, { useEffect, useState } from "react";
import { Gift, Copy, Share2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ReferralBanner() {
    const [data, setData] = useState(null);
    const [claiming, setClaiming] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/referrals/me");
            setData(data);
        } catch { /* ignore */ }
    }
    useEffect(() => { load(); }, []);

    if (!data) return null;

    const pctRef = Math.min(100, (data.referral_count / data.min_referrals) * 100);
    const pctHr = Math.min(100, (data.total_hours / data.min_hours) * 100);
    const eligible = data.eligible_for_reward && !data.reward_claimed;

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(data.share_url);
            toast.success("Referral link copied");
        } catch { toast.error("Could not copy — long-press the link instead"); }
    }
    async function shareNative() {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Come work with me on Infini HealthCare",
                    text: `Join me on Infini HealthCare and pick up shifts near you. Use my link:`,
                    url: data.share_url,
                });
            } catch { /* user cancelled */ }
        } else {
            copyLink();
        }
    }
    async function claim() {
        setClaiming(true);
        try {
            const { data: r } = await api.post("/referrals/claim");
            toast.success(r.message || "Reward claim submitted!");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not submit claim");
        } finally { setClaiming(false); }
    }

    return (
        <div
            data-testid="referral-banner"
            className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 mb-6"
        >
            <div className="flex items-start gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-2xl bg-emerald-700 text-white flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">
                        Refer &amp; earn
                    </div>
                    <h3 className="font-outfit font-semibold text-lg text-stone-900 tracking-tight">
                        Invite {data.min_referrals} caregivers, unlock a ${data.reward_amount_usd} Amazon gift card
                    </h3>
                    <p className="text-xs text-stone-600 font-dmsans mt-1">
                        When {data.min_referrals} people you refer join and work a combined <strong>{data.min_hours} hours</strong>, we&apos;ll email you a ${data.reward_amount_usd} Amazon gift card. Your code: <strong className="font-mono text-emerald-800">{data.referral_code}</strong>
                    </p>

                    {/* Progress bars */}
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        <ProgressBar
                            label="Referrals"
                            value={data.referral_count}
                            target={data.min_referrals}
                            pct={pctRef}
                            testid="referral-progress-count"
                        />
                        <ProgressBar
                            label="Combined hours"
                            value={data.total_hours}
                            target={data.min_hours}
                            pct={pctHr}
                            suffix="h"
                            testid="referral-progress-hours"
                        />
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap gap-2 items-center">
                        <button
                            data-testid="referral-copy-link"
                            onClick={copyLink}
                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-stone-200 hover:border-emerald-600 text-xs font-outfit font-semibold text-stone-800"
                        >
                            <Copy className="w-3.5 h-3.5" /> Copy link
                        </button>
                        <button
                            data-testid="referral-share"
                            onClick={shareNative}
                            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-xs font-outfit font-semibold"
                        >
                            <Share2 className="w-3.5 h-3.5" /> Share
                        </button>
                        {data.reward_claimed ? (
                            <span data-testid="referral-claimed" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                                <CheckCircle2 className="w-4 h-4" /> Reward claimed
                            </span>
                        ) : eligible ? (
                            <button
                                data-testid="referral-claim"
                                onClick={claim}
                                disabled={claiming}
                                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-outfit font-bold"
                            >
                                <Gift className="w-3.5 h-3.5" />
                                {claiming ? "Claiming…" : `Claim my $${data.reward_amount_usd} Amazon card`}
                            </button>
                        ) : (
                            <span className="text-[11px] text-stone-500 font-dmsans">
                                {data.min_referrals - data.referral_count > 0
                                    ? `${data.min_referrals - data.referral_count} more referral${data.min_referrals - data.referral_count === 1 ? "" : "s"} to go`
                                    : `${(data.min_hours - data.total_hours).toFixed(1)}h more to unlock`}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProgressBar({ label, value, target, pct, suffix, testid }) {
    return (
        <div>
            <div className="flex items-center justify-between text-[11px] font-dmsans text-stone-600 mb-1">
                <span>{label}</span>
                <span data-testid={testid} className="font-mono font-semibold text-stone-900">
                    {value}{suffix || ""} / {target}{suffix || ""}
                </span>
            </div>
            <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                <div
                    className="h-full bg-emerald-600 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}
